import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { playHaptic } from '@/shared/utils/haptics';

type InvoiceButtonVariant = 'primary' | 'secondary' | 'ghost';
type InvoiceButtonSize = 'compact' | 'default' | 'advance' | 'stamp';

interface InvoiceButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: InvoiceButtonVariant;
  size?: InvoiceButtonSize;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
}

const VARIANT_CLASSES: Record<InvoiceButtonVariant, string> = {
  primary:
    'border-transparent bg-h-primary-50 text-white md:hover:bg-h-primary-60',
  secondary:
    'border-h-neutral-90 bg-white text-h-text-heading md:hover:bg-h-neutral-94',
  ghost:
    'border-transparent bg-transparent text-h-neutral-20 md:hover:bg-h-neutral-94',
};

/**
 * Tamaños del handoff. El CTA de avance y el de timbrar se quedan en 16px
 * porque el repo exige `text-base` en todo CTA; los compactos son chips de
 * acción y sí bajan de tamaño.
 */
const SIZE_CLASSES: Record<InvoiceButtonSize, string> = {
  compact: 'min-h-9 px-3.5 py-1.5 text-sm',
  default: 'min-h-11 px-4 py-2.5 text-base',
  advance: 'min-h-12 px-6 py-3 text-base',
  stamp: 'min-h-14 px-7 py-3.5 text-base',
};

/**
 * Todo botón del flujo es píldora. Nunca se deshabilita un CTA primario como
 * único mecanismo de validación: el botón queda activo y quien llama muestra
 * qué falta (ver `missingRequiredFields`).
 */
export const InvoiceButton = forwardRef<HTMLButtonElement, InvoiceButtonProps>(
  (
    { variant = 'primary', size = 'default', iconLeft, iconRight, fullWidth, className, children, onClick, ...props },
    ref,
  ) => (
    <button
      ref={ref}
      type='button'
      onClick={(event) => {
        playHaptic('tap');
        onClick?.(event);
      }}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-full border font-medium',
        'transition-colors duration-150 active:scale-[0.96]',
        'focus-visible:ring-h-primary-50/20 focus-visible:border-h-primary-50 focus-visible:ring-3 focus-visible:outline-none',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}>
      {iconLeft}
      {children}
      {iconRight}
    </button>
  ),
);

InvoiceButton.displayName = 'InvoiceButton';
